/**
 * Componente Sidebar para navegación
 * Soporta menús con sub-menús (acordeón)
 * Versión desktop y mobile
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/auth/context/AuthContext';
import { getIcon } from '@/shared/utils/iconMapping';
import type { MenuItem } from '@/modules/auth/types';
import styles from './Sidebar.module.css';
import { FiChevronDown, FiChevronRight, FiLogOut, FiBook } from 'react-icons/fi';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface MenuItemComponentProps {
  item: MenuItem;
  level?: number;
  isExpanded?: boolean;
  onToggle?: () => void;
}

function MenuItemComponent({ item, isExpanded, onToggle }: MenuItemComponentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const IconComponent = getIcon(item.icon);
  const hasChildren = item.children && item.children.length > 0;
  const hasActiveChild = hasChildren && item.children?.some(child => 
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  );
  // El item está activo si es la ruta actual o si tiene un child activo
  const isActive = hasActiveChild || (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

  // Verificar permisos
  const hasPermission = useMemo(() => {
    if (!user) return false;
    
    if (item.required_role && !user.roles.includes(item.required_role)) {
      return false;
    }
    
    if (item.required_permission && !user.permissions.includes(item.required_permission)) {
      return false;
    }
    
    // required_module se puede verificar más adelante si es necesario
    return true;
  }, [user, item]);

  if (!hasPermission) {
    return null;
  }

  const handleClick = () => {
    if (hasChildren) {
      onToggle?.();
    } else {
      // Asegurar que el path funcione con hash routing
      const path = item.path.startsWith('/') ? item.path : `/${item.path}`;
      navigate(path);
    }
  };

  const handleChildClick = (e: React.MouseEvent, childPath: string) => {
    e.stopPropagation();
    // Asegurar que el path funcione con hash routing
    const path = childPath.startsWith('/') ? childPath : `/${childPath}`;
    navigate(path);
  };

  return (
    <div className={styles.menuItemContainer}>
      <button
        className={`${styles.menuItem} ${isActive || hasActiveChild ? styles.menuItemActive : ''}`}
        onClick={handleClick}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        <span className={styles.menuItemIcon}>
          <IconComponent size={20} />
        </span>
        <span className={styles.menuItemLabel}>{item.label}</span>
        {hasChildren && (
          <span className={styles.menuItemChevron}>
            {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          </span>
        )}
      </button>
      {hasChildren && isExpanded && (
        <div className={styles.subMenu}>
          {item.children?.map((child) => {
            const childHasPermission = !child.required_role || user?.roles.includes(child.required_role);
            const childHasPermission2 = !child.required_permission || user?.permissions.includes(child.required_permission);
            
            if (!childHasPermission || !childHasPermission2) {
              return null;
            }

            const isChildActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
            return (
              <button
                key={child.id}
                className={`${styles.menuItem} ${styles.subMenuItem} ${isChildActive ? styles.menuItemActive : ''}`}
                onClick={(e) => handleChildClick(e, child.path)}
              >
                <span className={styles.menuItemIcon}>
                  {child.icon ? (
                    (() => {
                      const ChildIcon = getIcon(child.icon);
                      return <ChildIcon size={18} />;
                    })()
                  ) : (
                    <span className={styles.subMenuDot} />
                  )}
                </span>
                <span className={styles.menuItemLabel}>{child.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const { menu, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Expandir automáticamente el item que tiene un child activo
  // Solo expandir uno a la vez (comportamiento de acordeón)
  useEffect(() => {
    if (!menu) return;
    
    // Buscar el item más específico que tiene un child activo
    let itemToExpand: string | null = null;
    let longestMatch = 0;
    
    menu.forEach((item) => {
      if (item.children && item.children.length > 0) {
        item.children.forEach(child => {
          if (location.pathname === child.path || location.pathname.startsWith(child.path + '/')) {
            const matchLength = child.path.length;
            // Si este match es más específico (más largo), usar este item
            if (matchLength > longestMatch) {
              longestMatch = matchLength;
              itemToExpand = item.id;
            }
          }
        });
      }
    });
    
    // Solo expandir el item más relevante
    if (itemToExpand) {
      setExpandedItems(new Set([itemToExpand]));
    }
  }, [menu, location.pathname]);

  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        // Si el item ya está expandido, cerrarlo
        next.delete(itemId);
      } else {
        // Si el item no está expandido, cerrar todos los demás y abrir solo este
        next.clear();
        next.add(itemId);
      }
      return next;
    });
  };

  // Filtrar menú según permisos del usuario
  const filteredMenu = useMemo(() => {
    if (!menu || !user) return [];
    
    return menu.filter((item) => {
      if (item.required_role && !user.roles.includes(item.required_role)) {
        return false;
      }
      if (item.required_permission && !user.permissions.includes(item.required_permission)) {
        return false;
      }
      return true;
    });
  }, [menu, user]);

  // Mostrar sidebar siempre, incluso si no hay menú (al menos el header)
  // Si no hay menú, solo mostrar el header

  return (
    <>
      {/* Overlay para mobile - solo cuando el sidebar está abierto */}
      {isOpen && (
        <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      )}
      
      <aside className={`${styles.sidebar} ${!isOpen ? styles.sidebarClosed : ''}`}>
        {/* Header con logo */}
        <div className={styles.sidebarHeader}>
          <div className={styles.logoContainer}>
            <FiBook className={styles.logoIcon} size={20} />
          </div>
          <div className={styles.branding}>
            <h1 className={styles.brandTitle}>JaviCore</h1>
            <p className={styles.brandSubtitle}>Panel de Control</p>
          </div>
        </div>

        {/* Lista de menús */}
        {filteredMenu.length > 0 && (
          <nav className={styles.menuList} aria-label="Navegación principal">
            {filteredMenu.map((item) => (
              <MenuItemComponent
                key={item.id}
                item={item}
                isExpanded={expandedItems.has(item.id)}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </nav>
        )}
        
        {/* Mensaje si no hay menú disponible */}
        {filteredMenu.length === 0 && menu && (
          <div className={styles.emptyMenu}>
            <p>No hay opciones de menú disponibles</p>
          </div>
        )}

        {/* Botón de cerrar sesión */}
        <div className={styles.logoutSection}>
          <button
            className={styles.logoutButton}
            onClick={() => {
              logout();
              navigate('/auth/login');
            }}
            aria-label="Cerrar sesión"
          >
            <span className={styles.logoutIcon}>
              <FiLogOut size={20} />
            </span>
            <span className={styles.logoutLabel}>Cerrar Sesión</span>
          </button>
        </div>
      </aside>
    </>
  );
}

